import React from 'react';
import { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useTitleDispatch } from '@components/TitleContextProvider';
import useActivityInitialData from '@behavior/activity/getActivityInitialData';
import usePostActivity from '@behavior/activity/usePostActivity';
import { useRiskAssessmentState } from './components/riskAssessment/useRiskAssessmentState';
import { useActivityState } from './useActivityState';
import useRiskAssessmentInitialData from '@behavior/activity/getRiskAssessmentInitialData';
import usePostRiskAssessment from '@behavior/activity/usePostRiskAssessment';
import { useActionState } from './components/actionForm/useActionState';
import usePostActionList from '@behavior/activity/usePostActionList';
import { useTraceabilityState } from './components/traceability/useTraceabilityState';
import useTraceabilityInitialData from '@behavior/activity/getTraceabilityInitialData';
import usePostTraceabilityData from '@behavior/activity/usePostTraceabilityData';
import {
  prepareAndSendActivityDTO,
  prepareAndSendRiskAssessmentDTO,
  prepareAndSendActionList,
  prepareAndSendTraceabilityForm,
} from './activityUtils';
import ActivityForm from './ActivityForm';
import {initSelectors} from "@behavior/init";
import useUser from '@behavior/init/getUserV2';
import {history} from "@src/store";
import {CREATE_MODE, entityType} from "@src/constants";

type Props = {
  mode: string;
};

const ActivityFormContainer = ({ mode }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { initialData } = useActivityInitialData(true);
  const { riskAssessmentInitial } = useRiskAssessmentInitialData(true);
  const { traceabilityInitial } = useTraceabilityInitialData();

  const { postActivity, isLoadingPostActivity } = usePostActivity();
  const { postRiskAssessment, isLoadingPostRiskAssessment } = usePostRiskAssessment();
  const postActionList = usePostActionList();
  const { t } = useTranslation();
  const dispatch = useTitleDispatch();

  const [currentStep, setCurrentStep] = useState(1);

  const activityState = useActivityState();
  const riskAssessmentState = useRiskAssessmentState({ mode });
  const actionState = useActionState();
  const traceabilityState = useTraceabilityState();
  const { user } = useUser();

  const { postTraceability, isLoadingTraceability } = usePostTraceabilityData({
    mode,
    activityId: activityState?.activity?.id,
  });

  const { activity, setActivity, addChemicalCard } = activityState;

  useEffect(() => {
    setIsLoading(isLoadingTraceability);
  }, [isLoadingTraceability]);

  const { riskAssessment, setRiskAssessment, riskAssessmentContainer, exposedPersonnel } =
    riskAssessmentState;

  const { actionCardContentList } = actionState;
  const { traceability, setTraceability, hseUnit, signatureDate, distributionList, assessorList } =
    traceabilityState;

  useEffect(() => {
    addChemicalCard();
  }, []);

  useEffect(() => {
    const pageTitle = t('page.activity.create.new');
    dispatch({
      pageTitle,
    });
  }, [mode, t]);

  useEffect(() => {
    if (initialData) {
      const { activity } = initialData;
      setActivity(activity);
    }
  }, [initialData]);

  useEffect(() => {
    if (riskAssessmentInitial) {
      setRiskAssessment(riskAssessmentInitial);
    }
  }, [riskAssessmentInitial]);

  useEffect(() => {
    if (traceabilityInitial) {
      setTraceability(traceabilityInitial);
    }
  }, [traceabilityInitial]);

  const onStepForward = () => {
    if (currentStep === 1) {
      prepareAndSendActivityDTO({
        activityState,
        postActivity,
      });
    }
    if (currentStep === 2) {
      prepareAndSendRiskAssessmentDTO({
        activity,
        riskAssessmentContainer,
        exposedPersonnel,
        setRiskAssessment,
        postRiskAssessment,
        riskAssessment,
        mode,
        riskAssessmentState,
        riskAssessmentInitial,
      });
    }
    if (currentStep === 3) {
      prepareAndSendActionList(actionCardContentList, postActionList, riskAssessment);
    }

    if (currentStep === 4) {
      prepareAndSendTraceabilityForm({
        traceability,
        hseUnit,
        signatureDate,
        riskAssessment,
        distributionList,
        assessorList,
        setTraceability,
        postTraceability,
      });
    }
    setCurrentStep(currentStep + 1);
  };

  const redirectIfNotEditAccess = () => {
    const redirect = mode === CREATE_MODE && user && !initSelectors.isActivityCanCreate(user, entityType.activity);
    if(redirect){
      history.replace('/forbidden');
      history.go(0);
    }
  }

  redirectIfNotEditAccess();

  return (
    <ActivityForm
      mode={mode}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onStepForward={onStepForward}
      initialData={initialData}
      activityState={activityState}
      riskAssessmentState={riskAssessmentState}
      actionState={actionState}
      traceabilityState={traceabilityState}
      isLoading={isLoading}
    />
  );
};

const mapStateToProps = (state: any, ownProps: any) => ({
  mode: ownProps.match.params.mode,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, null)
)(ActivityFormContainer) as React.FC<Props>;
